/* Cloudflare Turnstile — widget con feature-flag sin estados híbridos.
   El site key viene de GET /api/public/config (única fuente de verdad):
   sin key -> no se renderiza nada y los formularios funcionan como siempre;
   con key -> widget obligatorio (el backend rechaza envíos sin token). */
import { useEffect, useRef, useState } from "react";

import { api } from "@/lib/api";

let siteKeyPromise = null;
export const getTurnstileSiteKey = () => {
  if (!siteKeyPromise) {
    siteKeyPromise = api
      .get("/public/config")
      .then(({ data }) => data.turnstile_site_key || null)
      .catch(() => null);
  }
  return siteKeyPromise;
};

let scriptPromise = null;
const loadScript = () => {
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve) => {
      if (window.turnstile) return resolve(window.turnstile);
      const s = document.createElement("script");
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      s.async = true;
      s.onload = () => resolve(window.turnstile);
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
};

/** useTurnstile(): { siteKey, token, Widget } — token=null cuando está inactivo. */
export const TurnstileWidget = ({ onToken, className = "" }) => {
  const ref = useRef(null);
  const widgetIdRef = useRef(null);
  const [siteKey, setSiteKey] = useState(null);

  useEffect(() => {
    let mounted = true;
    getTurnstileSiteKey().then((key) => mounted && setSiteKey(key));
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!siteKey || !ref.current) return undefined;
    let disposed = false;
    loadScript().then((turnstile) => {
      if (disposed || !turnstile || !ref.current) return;
      widgetIdRef.current = turnstile.render(ref.current, {
        sitekey: siteKey,
        callback: (token) => onToken?.(token),
        "expired-callback": () => onToken?.(null),
        "error-callback": () => onToken?.(null),
        appearance: "always",
        theme: "light",
      });
    });
    return () => {
      disposed = true;
      if (widgetIdRef.current != null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* no-op */
        }
      }
    };
  }, [siteKey, onToken]);

  if (!siteKey) return null; // Turnstile inactivo: formulario idéntico a hoy
  return <div ref={ref} className={className} data-testid="turnstile-widget" />;
};
